import { useEffect, useState } from 'react';

import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocalStorage } from 'react-use';

import Button from '../../../../components/buttons/Button';
import { ErrorMessage } from '../../../../components/ErrorMessage/ErrorMessage';
import type { WizardChildProps } from '../../../../components/Wizard/types';
import EmailAndPasswordInputs from '../../../../features/auth/EmailAndPasswordInputs/EmailAndPasswordInputs';
import { ResponsibleSocialPlay } from '../../../../features/auth/ResponsibleSocialPlayTerms/ResponsibleSocialPlayTerms';
import SignUpConsents from '../../../../features/auth/SignUpConsents/SignUpConsents';
import {
  getEmailErrorMessageMap,
  getPasswordErrorMessageMap,
} from '../../../../features/auth/SignUpRegister/constants';
import { ThirdPartyAuth } from '../../../../features/auth/ThirdPartyAuth/ThirdPartyAuth';
import { useSignUpWizardContext } from '../../../../features/auth/wizards/signUpWizard.provider';
import { useHandleSignUpWizard } from '../../../../features/auth/wizards/useHandleSIgnUpWizard';
import { useMarketingConsents } from '../../../../hooks/useMarketingConsents';
import { useFlag } from '../../../../services/launchDarkly';
import { useStores } from '../../../../stores/useStores';

import { useAuthForm } from './hooks/useAuthForm';
import classes from './SignupSection.module.scss';

interface SignupFormProps {
  onSignupSuccess?: () => void;
  wizardProps?: WizardChildProps;
  onSwitchToLogin: () => void;
}

export const SignupForm = observer(({ onSignupSuccess, wizardProps, onSwitchToLogin }: SignupFormProps) => {
  const { t } = useTranslation();
  const { marketingConsentStore, liveGeoStore } = useStores();
  const [showRSGDialog, setShowRSGDialog] = useState(false);
  const { email, password, isLoading, onEmailChange, onPasswordChange } = useAuthForm(onSignupSuccess);
  const { handleSignUpWizard } = useHandleSignUpWizard();

  const { error, updateContextData } = useSignUpWizardContext();
  const [_signUpMarketingConsentsLS, setSignUpMarketingConsentsLS] = useLocalStorage<Record<string, boolean>>(
    'signup-marketing-consents',
    {},
  );

  const [marketingConsents, setMarketingConsents] = useState<Record<string, boolean>>({});
  const [isAgeConfirmed, setIsAgeConfirmed] = useState(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [approveError, setApproveError] = useState('');
  const { countryCode } = liveGeoStore.rawGeo ?? {};

  const { signUpMarketingConsents, fetchingSignUpMarketingConsent } = marketingConsentStore;
  const { onChangeMarketingConsent, areAllOptionalConsentsChecked } = useMarketingConsents({
    initialMarketingConsents: signUpMarketingConsents,
    fetchingMarketingConsents: fetchingSignUpMarketingConsent,
    localStorageKey: 'signup-marketing-consents',
  });
  const responsible_gaming_dialog = useFlag('responsible_gaming_dialog', ['']);
  const shouldShowRSGDialog = responsible_gaming_dialog.includes(countryCode || 'US') && !areAllOptionalConsentsChecked;

  const isApprovalFieldsValid = Boolean(isAgeConfirmed && isTermsAccepted);

  useEffect(() => {
    if (!fetchingSignUpMarketingConsent) {
      if (signUpMarketingConsents.length > 0) {
        const map = signUpMarketingConsents.reduce(
          (acc, consent) => {
            acc[consent.id] = false;
            return acc;
          },
          {} as Record<string, boolean>,
        );

        setMarketingConsents(map);
      } else {
        setMarketingConsents({});
      }
    }
  }, [signUpMarketingConsents, fetchingSignUpMarketingConsent]);

  const validateApprovalFields = () => {
    if (!isApprovalFieldsValid) {
      setApproveError(t('auth.approvalConditionsRequired'));
      return false;
    }
    setSignUpMarketingConsentsLS(marketingConsents);
    setApproveError('');
    return true;
  };

  const handleProviderSignUp = () => {
    if (!validateApprovalFields()) {
      return false;
    }
    updateContextData({ provider: 'google' });

    if (shouldShowRSGDialog) {
      setShowRSGDialog(true);
      return false;
    }
    return true;
  };

  const handleEmailSignUp = () => {
    const credentials = { email: email.trim(), password: password.trim() };

    if (isLoading) {
      return;
    }

    if (!validateApprovalFields()) {
      return;
    }

    if (!email || !password) {
      updateContextData({ error: t('landing.direct.form.bothFieldsRequired') });
      return;
    }

    if (shouldShowRSGDialog) {
      updateContextData({ provider: 'email', credentials });
      setShowRSGDialog(true);
    } else {
      updateContextData({ provider: 'email', credentials });
      handleSignUpWizard({
        provider: 'email',
        action: 'sign_up',
        credentials,
        wizardProps,
      });
    }
  };

  useEffect(() => {
    if (error) {
      setShowRSGDialog(false);
    }
  }, [error]);

  const onChangeMarketingConsentCheckBox = (checked: boolean, consentId: string) => {
    onChangeMarketingConsent(checked, consentId);
    setMarketingConsents((prev) => ({ ...prev, [consentId]: checked }));
  };

  const handleCloseResponsiblePlay = () => {
    setShowRSGDialog(false);
  };

  return (
    <div className={classes.signupSection}>
      {showRSGDialog && (
        <div className={classes.responsiblePlayPopup}>
          <div className={classes.responsiblePlayOverlay} onClick={handleCloseResponsiblePlay} />
          <div className={classes.responsiblePlayContent}>
            <ResponsibleSocialPlay
              {...(wizardProps as WizardChildProps)}
              wizardProps={wizardProps}
              onClose={handleCloseResponsiblePlay}
            />
          </div>
        </div>
      )}
      <div className={classes.googleSignupWrapper}>
        <ThirdPartyAuth
          action="Sign Up"
          className={classes.thirdPartyAuth}
          excludedProviders={['facebook', 'apple']}
          hideLoader={true}
          onLoginAttempt={handleProviderSignUp}
        />
      </div>

      <div className={classes.orDivider}>
        <span className={classes.orText}>{t('common.or')}</span>
      </div>

      <div className={classes.emailFormWrapper}>
        <EmailAndPasswordInputs
          className={classes.emailAndPasswordInputs}
          disabled={isLoading}
          emailErrorMessageMap={getEmailErrorMessageMap(t)}
          passwordErrorMessageMap={getPasswordErrorMessageMap(t)}
          size="sm"
          onChangeEmail={onEmailChange}
          onChangePassword={onPasswordChange}
        />
      </div>

      <div className={classes.consentsWrapper}>
        <SignUpConsents
          approveError={approveError}
          isAgeConfirmed={isAgeConfirmed}
          isTermsAccepted={isTermsAccepted}
          marketingConsents={marketingConsents}
          setIsAgeConfirmed={setIsAgeConfirmed}
          setIsTermsAccepted={setIsTermsAccepted}
          signUpMarketingConsents={signUpMarketingConsents}
          onChangeMarketingConsent={onChangeMarketingConsentCheckBox}
        />
      </div>

      <ErrorMessage noSpaceWhenEmpty className={classes.authErrorMessage}>
        {error}
      </ErrorMessage>

      <Button
        btnType="main"
        className={classes.createAccountButton}
        dataTestId="signup-create-account-btn"
        disabled={isLoading}
        type="button"
        onClick={handleEmailSignUp}
      >
        {t('auth.createAccount')}
      </Button>

      <div className={classes.loginLink} data-testid="direct-signup-already-have-account">
        <Trans
          components={{
            question: <span />,
            login: <span className={classes.loginLinkText} onClick={onSwitchToLogin} />,
          }}
          i18nKey="landing.direct.alreadyHaveAccountLogIn"
        />
      </div>
    </div>
  );
});
